// Architecture.jsx
import { useCallback, useEffect, useRef, useState } from "react";
import ReactFlow, {
  Controls,
  Background,
  applyNodeChanges,
  applyEdgeChanges,
  useReactFlow,
  ReactFlowProvider,
  Node,
  Edge,
  BackgroundVariant,
  NodeChange,
  EdgeChange
} from "reactflow";
import "reactflow/dist/style.css";

interface NodeData {
  label: string;
}

const generateLayout = (nodes: any[]): Node<NodeData>[] => {
  if (!nodes || !Array.isArray(nodes)) return [];
  
  return nodes.map((node, index) => ({
    id: node.id,
    data: { label: node.name },
    position: { x: index * 250, y: (index % 2) * 200 },
    draggable: true,
    style: {
      fontSize: "16px",
      color: "#fff", // White text is good for both light/dark nodes
      background: "#1e293b", // Darker blue gray background
      border: "1px solid #475569",
      padding: "10px",
      borderRadius: "8px",
      textAlign: "center" as const,
      width: "auto",
      minWidth: "150px",
      maxWidth: "100%",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.2)",
    },
  }));
};

interface FlowChartComponentProps {
  testData: {
    nodes: any[];
  };
}

function FlowChartComponent({ testData }: FlowChartComponentProps) {
  const { fitView, zoomIn, zoomOut } = useReactFlow();
  const containerRef = useRef<HTMLDivElement>(null);
  const nodesRef = useRef<HTMLDivElement>(null);
  
  const isValidData = testData && testData.nodes && Array.isArray(testData.nodes);
  
  const [nodes, setNodes] = useState<Node<NodeData>[]>(isValidData ? generateLayout(testData.nodes) : []);
  const [edges, setEdges] = useState<Edge[]>(
    isValidData ? testData.nodes.flatMap((node) =>
      (node.dependencies || []).map((dep) => ({
        id: `e${node.id}-${dep}`,
        source: node.id,
        target: dep,
        animated: true,
        style: { stroke: "#10b981", strokeWidth: 2 }, // Brighter green for light mode
      }))
    ) : []
  );
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => setNodes((nds) => applyNodeChanges(changes, nds)),
    []
  );
  
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    []
  );

  // Handle node selection
  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    setSelectedNodeId(node.id);
  }, []);

  // Update nodes and edges based on selection
  useEffect(() => {
    if (!isValidData) return;

    // Update nodes
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        style: {
          ...node.style,
          color: "#fff", // White text for both modes
          background: node.id === selectedNodeId 
            ? "#0f766e" // Teal for selected
            : "#1e293b", // Dark blue-gray for others
          border: node.id === selectedNodeId 
            ? "2px solid #34d399" 
            : "1px solid #475569",
          boxShadow: node.id === selectedNodeId 
            ? "0 0 8px 2px rgba(52, 211, 153, 0.5)" 
            : "0 4px 6px -1px rgba(0, 0, 0, 0.2)",
        },
      }))
    );

    // Update edges
    setEdges((eds) =>
      eds.map((edge) => ({
        ...edge,
        style: {
          stroke: 
            selectedNodeId && 
            (edge.source === selectedNodeId || edge.target === selectedNodeId)
              ? "#f472b6"  // Bright pink for selected paths
              : "#10b981", // Brighter green for light mode
          strokeWidth: 
            selectedNodeId && 
            (edge.source === selectedNodeId || edge.target === selectedNodeId)
              ? 3          // Thicker line for selected paths
              : 2,         // Default width
        },
      }))
    );
  }, [selectedNodeId, isValidData, testData]);

  useEffect(() => {
    if (isValidData) {
      setNodes(generateLayout(testData.nodes));
      setEdges(testData.nodes.flatMap((node) =>
        (node.dependencies || []).map((dep) => ({
          id: `e${node.id}-${dep}`,
          source: node.id,
          target: dep,
          animated: true,
          style: { stroke: "#10b981", strokeWidth: 2 },
        }))
      ));
    }
  }, [testData]);

  useEffect(() => {
    if (nodesRef.current && nodes.length > 0) {
      fitView({ duration: 800 });
    }
  }, [nodes, fitView]);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  if (!isValidData) {
    return (
      <div className="w-full h-64 flex items-center justify-center bg-white dark:bg-[#1a1c23] rounded-lg border border-gray-300 dark:border-gray-700 text-gray-500 dark:text-gray-400">
        No architecture data available or data is invalid.
      </div>
    );
  }

  return (
    <div ref={containerRef} className="w-full h-full rounded-lg border border-gray-300 dark:border-gray-700 relative bg-gray-200 dark:bg-slate-900">
      <ReactFlow
        ref={nodesRef}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        className="bg-gray-200 dark:bg-slate-900" // Slightly darker gray for light mode
      >
        <Controls showZoom={false} showInteractive={false} />
        <Background variant={"dots" as BackgroundVariant} gap={12} size={1} color="#64748b" className="!bg-gray-250 dark:!bg-slate-900" /> {/* Updated background class */}

        <div className="absolute top-4 right-4 z-50 flex flex-col gap-2">
          <button onClick={() => zoomIn()} className="control-btn">+</button>
          <button onClick={() => zoomOut()} className="control-btn">-</button>
          <button onClick={toggleFullScreen} className="control-btn">⤢</button>
        </div>
      </ReactFlow>
    </div>
  );
}

interface FlowChartProps {
  testData: {
    nodes: any[];
  };
}

const FlowChart = ({ testData }: FlowChartProps) => {
  return (
    <ReactFlowProvider>
      <FlowChartComponent testData={testData} />
    </ReactFlowProvider>
  );
};

export default FlowChart;

const controlButtonStyles = `
  .control-btn {
    padding: 10px;
    font-size: 18px;
    cursor: pointer;
    background: #f1f5f9;
    color: #1e293b;
    border: 1px solid #cbd5e1;
    border-radius: 5px;
    transition: background 0.2s;
  }
  .control-btn:hover {
    background: #e2e8f0;
  }
  
  @media (prefers-color-scheme: dark) {
    .control-btn {
      background: #0f172a;
      color: white;
      border: 1px solid #334155;
    }
    .control-btn:hover {
      background: #1e293b;
    }
  }
`;

if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = controlButtonStyles;
  document.head.appendChild(styleSheet);
}